import { getSseAuthToken } from './sseAuth';

export type SseStatus = 'connecting' | 'open' | 'closed' | 'error';

export interface SseEvent {
    /** Event type from the `event:` field; defaults to `message`. */
    event: string;
    /** Joined `data:` payload (multiple data lines joined with `\n`). */
    data: string;
    /** Last `id:` field, if present. */
    id?: string;
}

export interface SseClientOptions {
    /** Path appended to the base URL, e.g. `/events`. */
    path: string;
    onMessage: (event: SseEvent) => void;
    onStatusChange?: (status: SseStatus) => void;
    onError?: (error: unknown) => void;
    /** Called once per successful handshake with the raw response (e.g. to read headers). */
    onOpen?: (response: Response) => void;
    /** Base URL override; defaults to the Go backend. */
    baseUrl?: string;
    initialRetryDelayMs?: number;
    maxRetryDelayMs?: number;
}

const DEFAULT_INITIAL_DELAY = 1000;
const DEFAULT_MAX_DELAY = 30000;

/**
 * Framework-agnostic authenticated SSE client.
 *
 * Uses `fetch` + `ReadableStream` (not the native `EventSource`) so it can send
 * an `Authorization: Bearer` header, matching the rest of the app's auth model.
 * It owns its own reconnect loop with exponential backoff and re-mints the auth
 * token on every connect attempt — the backend validates the token at handshake
 * only, so an open stream survives token expiry but a reconnect needs a fresh one.
 */
export class SseClient {
    private controller: AbortController | null = null;
    private stopped = false;
    private retryAttempt = 0;
    private readonly baseUrl: string;

    constructor(private readonly options: SseClientOptions) {
        this.baseUrl = options.baseUrl ?? import.meta.env.VITE_APP_GO_BACKEND_URL;
    }

    /** Opens the connection and keeps it open, reconnecting until `stop()`. */
    start(): void {
        this.stopped = false;
        void this.runLoop();
    }

    /** Aborts the current connection and prevents further reconnects. */
    stop(): void {
        this.stopped = true;
        this.controller?.abort();
        this.controller = null;
        this.setStatus('closed');
    }

    private setStatus(status: SseStatus): void {
        this.options.onStatusChange?.(status);
    }

    private async runLoop(): Promise<void> {
        while (!this.stopped) {
            try {
                await this.connect();
                // connect() resolves when the server closes the stream cleanly.
            } catch (error) {
                if (this.stopped || this.isAbortError(error)) {
                    return;
                }
                this.options.onError?.(error);
                this.setStatus('error');
            }
            if (this.stopped) return;
            await this.waitBackoff();
        }
    }

    private async connect(): Promise<void> {
        this.setStatus('connecting');
        const token = await getSseAuthToken();
        this.controller = new AbortController();

        const response = await fetch(this.baseUrl + this.options.path, {
            method: 'GET',
            headers: {
                Accept: 'text/event-stream',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: this.controller.signal,
        });

        if (!response.ok || !response.body) {
            throw new Error(`SSE connection failed with status ${response.status}`);
        }

        this.retryAttempt = 0;
        this.setStatus('open');
        this.options.onOpen?.(response);
        await this.readStream(response.body);
        this.setStatus('closed');
    }

    private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            // Normalize CRLF line endings; a lone trailing CR stays buffered for the next chunk.
            buffer = buffer.replace(/\r\n/g, '\n');

            let sepIndex: number;
            while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
                const rawFrame = buffer.slice(0, sepIndex);
                buffer = buffer.slice(sepIndex + 2);
                const event = this.parseFrame(rawFrame);
                if (event) this.options.onMessage(event);
            }
        }
    }

    private parseFrame(raw: string): SseEvent | null {
        let eventType = 'message';
        let id: string | undefined;
        const dataLines: string[] = [];

        for (const line of raw.split('\n')) {
            // Blank lines and comments (`:` keep-alive) carry no fields.
            if (line === '' || line.startsWith(':')) continue;

            const colon = line.indexOf(':');
            const field = colon === -1 ? line : line.slice(0, colon);
            let val = colon === -1 ? '' : line.slice(colon + 1);
            // Strip a single leading space after the colon, per the SSE spec.
            if (val.startsWith(' ')) val = val.slice(1);

            switch (field) {
                case 'event':
                    eventType = val;
                    break;
                case 'data':
                    dataLines.push(val);
                    break;
                case 'id':
                    id = val;
                    break;
                // `retry` is ignored — this client owns its backoff.
            }
        }

        if (dataLines.length === 0) return null;
        return { event: eventType, data: dataLines.join('\n'), id };
    }

    private waitBackoff(): Promise<void> {
        const initial = this.options.initialRetryDelayMs ?? DEFAULT_INITIAL_DELAY;
        const max = this.options.maxRetryDelayMs ?? DEFAULT_MAX_DELAY;
        const base = Math.min(max, initial * 2 ** this.retryAttempt);
        this.retryAttempt += 1;
        // Full jitter within the upper half of the window: [base/2, base).
        const delay = base / 2 + (base / 2) * Math.random();
        return new Promise((resolve) => setTimeout(resolve, delay));
    }

    private isAbortError(error: unknown): boolean {
        return error instanceof DOMException && error.name === 'AbortError';
    }
}
